import { useEffect, useRef, useState } from 'react'
import {Building2, CheckCircle2, AlertCircle, Save, ImageIcon, Upload, Loader2, Trash2, Sparkles} from 'lucide-react'
import toast from 'react-hot-toast'
import { CompanyProfile, CompanyProfileInput, useCompanyProfile, AccountType } from '@/hooks/useCompanyProfile'
import { uploadOneFile } from '@/lib/upload'

interface CompanyProfileFormProps {
  creator: string
  email?: string
  accountType: AccountType
  /** Couleur thématique : recruteur (bleu/sombre) vs organisme (ambre) */
  theme?: 'recruiter' | 'organisme'
  /** Callback appelé après sauvegarde réussie */
  onSaved?: (profile: CompanyProfile) => void
  /** Texte d'introduction */
  intro?: string
}

const EMPTY: CompanyProfileInput = {
  raisonSociale: '',
  siret: '',
  adresse: '',
  telephone: '',
  responsableNom: '',
  responsableFonction: '',
  logoUrl: '',
}

export default function CompanyProfileForm({
  creator,
  email,
  accountType,
  theme = 'recruiter',
  onSaved,
  intro,
}: CompanyProfileFormProps) {
  const { profile, loading, saving, error, upsert, isComplete } = useCompanyProfile(
    creator,
    email,
    accountType
  )
  const [form, setForm] = useState<CompanyProfileInput>(EMPTY)
  const [siretError, setSiretError] = useState<string | null>(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (profile) {
      setForm({
        raisonSociale: profile.raisonSociale || '',
        siret: profile.siret || '',
        adresse: profile.adresse || '',
        telephone: profile.telephone || '',
        responsableNom: profile.responsableNom || '',
        responsableFonction: profile.responsableFonction || '',
        logoUrl: profile.logoUrl || '',
      })
    }
  }, [profile])

  const accent =
    theme === 'organisme'
      ? {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-900',
          ring: 'focus:ring-amber-500',
          btn: 'bg-amber-600 hover:bg-amber-700',
          iconBg: 'bg-amber-100 text-amber-700',
        }
      : {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          ring: 'focus:ring-blue-500',
          btn: 'bg-gray-900 hover:bg-black',
          iconBg: 'bg-blue-100 text-blue-700',
        }

  const handleLogoSelect = async (file: File | null | undefined) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Merci de sélectionner un fichier image (PNG, JPG, SVG, WebP).')
      return
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Le logo ne doit pas dépasser 4 Mo.')
      return
    }
    setUploadingLogo(true)
    try {
      const url = await uploadOneFile(file)
      if (url) {
        setForm((prev) => ({ ...prev, logoUrl: url }))
        toast.success('Logo téléversé — n’oubliez pas d’enregistrer.')
      } else {
        toast.error('Échec du téléversement du logo.')
      }
    } catch (err) {
      console.error('[CompanyProfileForm] logo upload failed', err)
      toast.error("Impossible de téléverser le logo.")
    } finally {
      setUploadingLogo(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleLogoRemove = () => {
    setForm((prev) => ({ ...prev, logoUrl: '' }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSiretError(null)

    // SIRET désormais optionnel : on valide le format seulement s'il est renseigné
    const cleanSiret = (form.siret || '').replace(/\s+/g, '')
    if (cleanSiret && !/^\d{14}$/.test(cleanSiret)) {
      setSiretError('Si renseigné, le numéro SIRET doit contenir exactement 14 chiffres.')
      return
    }

    // Adresse complète désormais optionnelle : seuls raison sociale, téléphone et responsable restent requis
    if (!form.raisonSociale.trim() || !form.telephone.trim() || !form.responsableNom.trim()) {
      toast.error('Merci de remplir tous les champs obligatoires.')
      return
    }

    const saved = await upsert({ ...form, siret: cleanSiret })
    if (saved) {
      toast.success('Coordonnées enregistrées.')
      if (onSaved) onSaved(saved)
    } else {
      toast.error("Échec de l'enregistrement.")
    }
  }

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500">
        Chargement de vos coordonnées…
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accent.iconBg}`}>
          <Building2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">Mes coordonnées</h2>
            {isComplete ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold">
                <CheckCircle2 className="w-3 h-3" />
                Complétées
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold">
                <AlertCircle className="w-3 h-3" />
                À compléter
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {intro ||
              "Ces informations sont obligatoires pour publier une offre. Elles ne sont jamais affichées sur les annonces ni accessibles aux candidats."}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Logo upload — optionnel mais encouragé */}
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Logo de l’entreprise{' '}
            <span className="font-normal text-gray-500">(optionnel mais recommandé)</span>
          </label>
          <div
            className={`flex items-center gap-4 p-4 rounded-xl border border-dashed ${accent.border} ${accent.bg}`}
          >
            <div className="w-20 h-20 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {form.logoUrl ? (
                <img
                  src={form.logoUrl}
                  alt="Logo de l’entreprise"
                  className="w-full h-full object-contain"
                />
              ) : (
                <ImageIcon className="w-7 h-7 text-gray-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold ${accent.text} flex items-center gap-1`}>
                <Sparkles className="w-3.5 h-3.5" />
                Mettez votre logo en avant sur vos annonces
              </p>
              <p className="text-[11px] text-gray-600 mt-1 leading-snug">
                Les annonces accompagnées d’un logo reçoivent en moyenne plus de candidatures. PNG, JPG, SVG ou WebP — 4 Mo max. Carré ou logo sur fond transparent recommandé.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-800 text-xs font-semibold hover:border-gray-400 disabled:opacity-50"
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {form.logoUrl
                    ? uploadingLogo
                      ? 'Téléversement…'
                      : 'Remplacer le logo'
                    : uploadingLogo
                      ? 'Téléversement…'
                      : 'Téléverser un logo'}
                </button>
                {form.logoUrl && !uploadingLogo && (
                  <button
                    type="button"
                    onClick={handleLogoRemove}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Retirer
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleLogoSelect(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Raison sociale <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={form.raisonSociale}
            onChange={(e) => setForm({ ...form, raisonSociale: e.target.value })}
            placeholder="Ex : Centre Aquatique du Lac SARL"
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 ${accent.ring}`}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Numéro SIRET{' '}
            <span className="font-normal text-gray-500">(optionnel)</span>
          </label>
          <input
            type="text"
            value={form.siret}
            onChange={(e) => {
              setForm({ ...form, siret: e.target.value })
              if (siretError) setSiretError(null)
            }}
            placeholder="14 chiffres, ex : 12345678901234"
            maxLength={20}
            className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 ${
              siretError ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 ' + accent.ring
            }`}
          />
          {siretError && <p className="mt-1 text-[11px] text-red-600">{siretError}</p>}
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Téléphone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            required
            value={form.telephone}
            onChange={(e) => setForm({ ...form, telephone: e.target.value })}
            placeholder="Ex : 04 78 12 34 56"
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 ${accent.ring}`}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Adresse complète{' '}
            <span className="font-normal text-gray-500">(optionnel)</span>
          </label>
          <input
            type="text"
            value={form.adresse}
            onChange={(e) => setForm({ ...form, adresse: e.target.value })}
            placeholder="Ex : 12 rue des Sources, 69003 Lyon"
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 ${accent.ring}`}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Nom du responsable <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={form.responsableNom}
            onChange={(e) => setForm({ ...form, responsableNom: e.target.value })}
            placeholder="Ex : Marie Dupont"
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 ${accent.ring}`}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Fonction (optionnel)
          </label>
          <input
            type="text"
            value={form.responsableFonction || ''}
            onChange={(e) => setForm({ ...form, responsableFonction: e.target.value })}
            placeholder="Ex : Directrice RH"
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 ${accent.ring}`}
          />
        </div>

        <div className="md:col-span-2 flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving}
            className={`inline-flex items-center gap-2 ${accent.btn} text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50`}
          >
            <Save className="w-4 h-4" />
            {saving ? 'Enregistrement…' : profile ? 'Mettre à jour' : 'Enregistrer mes coordonnées'}
          </button>
        </div>
      </form>
    </div>
  )
}
